import { Box, Skeleton, useMediaQuery } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import {
  type Appearance,
  loadStripe,
  type Stripe,
  type StripeElementsOptions,
} from '@stripe/stripe-js';
import { usePaymentData } from 'api/payment/payment.queries';
import { useSubmission } from 'api/submission';
import { AMBestRating } from 'components/AMBestRating';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import PaymentLayout from 'layouts/paymentLayout';
import theme from 'providers/theme/theme';
import { type FC, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ErrorComponent from 'routes/Payments/components/Error';
import { useFullscreenLoader } from 'store/features/UI';
import CheckoutForm from './components/CheckoutForm';
import PaymentHeader from './components/PaymentHeader';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentSummary from './components/PaymentSummary';
import PurchasedInfo from './components/PurchasedInfo';
import { PaymentWrapperStyles } from './Payment.styles';

// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
let stripePromise: Stripe | null;

// Timeout duration for loading the Stripe payment form
const PAYMENT_FORM_LOADING_TIMEOUT = 5000; // 5 seconds

interface ErrorState {
  message?: string;
}

const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: theme.palette.secondary.main,
    fontFamily: 'Nexa, sans-serif',
    borderRadius: '0px',
    focusBoxShadow: `0 0 0 1px ${theme.palette.secondary.main}`,
    focusOutline: 'none',
  },
  labels: 'floating',
  rules: {
    '.Input, .Tab, .PickerItem': {
      borderColor: `${theme.palette.customColors.beigeLight}`,
    },
    '.Tab--selected': {
      backgroundColor: `${theme.palette.customColors.lightYellow}`,
    },
  },
};

/**
 * Payment Component
 *
 * This component handles the payment process using Stripe Elements.
 *
 * Features:
 * - Fetches PaymentIntent and Stripe configuration from the backend.
 * - Displays payment details and a payment form using Stripe Elements.
 * - Handles loading state and error handling.
 */
const Payment: FC = () => {
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState<ErrorState | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { setFullscreenLoading, isFullscreenLoading } = useFullscreenLoader();

  const { id: submissionId } = useParams() as { id: string };

  const {
    data: paymentData,
    isLoading,
    error: paymentError,
  } = usePaymentData(submissionId as string);

  const { data: submission, isLoading: isSubmissionLoading } = useSubmission(submissionId);

  const isBusinessRequiredDocumentsMissing = !!submission?.are_business_required_documents_missing;

  const isPurchased = !!paymentData?.is_purchased;

  const isPaymentCompleted = searchParams.get('payment_completed');

  const [checkoutFormIsRendered, setCheckoutFormIsRendered] = useState(false);

  const handleSetCheckoutFormIsRendered = (isRendered: boolean) => {
    setCheckoutFormIsRendered(isRendered);
  };

  const isPageLoading = isLoading;

  const fetchPaymentIntent = async () => {
    if (!clientSecret && paymentData) {
      try {
        stripePromise = await loadStripe(paymentData.publishable_key);
        if (!stripePromise) throw new Error('Stripe init failed: loadStripe returned null');

        setClientSecret(paymentData.client_secret);
      } catch (e) {
        const err = e as unknown as ErrorState;
        setError(err);
      }
    }
  };

  const options: StripeElementsOptions = useMemo(
    () => ({
      fonts: [
        {
          family: 'Nexa',
          src: 'local(Nexa), url(/fonts/Nexa/NexaHeavy.woff2) format(woff2)',
          weight: '900',
        },
        {
          family: 'Nexa',
          src: 'local(Nexa), url(/fonts/Nexa/NexaBold.woff2) format(woff2)',
          weight: '700',
        },
        {
          family: 'Nexa',
          src: 'local(Nexa), url(/fonts/Nexa/NexaRegular.woff2) format(woff2)',
          weight: '400',
        },
      ],
      clientSecret: paymentData?.client_secret,
      appearance,
    }),
    [paymentData?.client_secret, appearance],
  );

  useEffect(() => {
    fetchPaymentIntent();
  }, [paymentData]);

  useLayoutEffect(() => {
    if (paymentError) {
      displayBackendErrorMessage(paymentError);
      navigate(`/submission/${submissionId}`, { replace: true });
      setFullscreenLoading(false);
    }

    return () => {
      setFullscreenLoading(false);
    };
  }, [paymentError]);

  useLayoutEffect(() => {
    if (isBusinessRequiredDocumentsMissing && !isPurchased && !isPaymentCompleted) {
      navigate(`/submission/${submissionId}`, { replace: true });
      setFullscreenLoading(false);
    }
  }, [isBusinessRequiredDocumentsMissing, isPurchased, isPaymentCompleted]);

  // Show loading screen if page is loading or checkout form isn't rendered while quote isn't purchased
  useLayoutEffect(() => {
    if (error && isFullscreenLoading) {
      setFullscreenLoading(false);
    }

    if (
      !error &&
      (isPageLoading || (!checkoutFormIsRendered && !isPaymentCompleted && !isPurchased))
    ) {
      setFullscreenLoading(true, 'Loading payment information');
    } else if (!error) {
      setFullscreenLoading(false);
    }
  }, [isPageLoading, isPurchased, checkoutFormIsRendered, isPaymentCompleted, error]);

  useLayoutEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (
      (isPageLoading || isFullscreenLoading) &&
      !checkoutFormIsRendered &&
      stripePromise &&
      paymentData
    ) {
      timeoutId = setTimeout(() => {
        setError({
          message:
            'Stripe payment form loading error. Please refresh the page or contact support team.',
        });
      }, PAYMENT_FORM_LOADING_TIMEOUT);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isPageLoading, isFullscreenLoading, checkoutFormIsRendered, stripePromise, paymentData]);

  if (error) return <ErrorComponent error={error} />;

  if (isPaymentCompleted && stripePromise)
    return (
      <PaymentLayout>
        <Elements stripe={stripePromise}>
          <PaymentSuccess />
        </Elements>
      </PaymentLayout>
    );

  if (isPurchased && !isPageLoading) {
    return (
      <PaymentLayout>
        <PurchasedInfo />
      </PaymentLayout>
    );
  }

  return (
    <PaymentLayout>
      <Box
        sx={[
          PaymentWrapperStyles,
          isMobileLayout && {
            minHeight: 'inherit',
          },
        ]}
      >
        <PaymentHeader />

        <PaymentSummary />

        <Box>
          {!isPageLoading &&
          !isSubmissionLoading &&
          clientSecret &&
          paymentData &&
          stripePromise ? (
            <Elements options={options} stripe={stripePromise}>
              <CheckoutForm
                paymentData={paymentData}
                submissionId={submissionId}
                paperlessOptIn={!!submission?.paperless_opt_in}
                handleSetCheckoutFormIsRendered={handleSetCheckoutFormIsRendered}
              />
            </Elements>
          ) : (
            <Skeleton height="300px" variant="rectangular" />
          )}
        </Box>

        <AMBestRating placement="end" />
      </Box>
    </PaymentLayout>
  );
};

export default Payment;
